<script setup lang="ts">
import Text from "@/cms/primitives/Text/Text.vue";
import { ChevronRight } from "lucide-vue-next";
import { ref } from "vue";
import Input from "@/cms/components/ui/input/Input.vue";
import LoadingSpinner from "@/cms/components/LoadingSpinner/LoadingSpinner.vue";
import type { SiteSwitcherSite } from "@/components/CMSComponents/SiteSwitcher/useSiteSwitcher.ts";
import { debounce } from "lodash";
import RocketLogo from "@/assets/img/logo.png";

defineProps<{
  sites: SiteSwitcherSite[];
  isInternal: boolean;
  isInternalSiteSearchLoading: boolean;
}>();

const emit = defineEmits<{
  (e: "go-to-site", site: SiteSwitcherSite): void;
  (e: "search-site-name", name: string): void;
}>();

const hoverKey = ref(-1);
const searchQuery = ref("");

const debouncedSearch = debounce((value: string) => {
  // eslint-disable-next-line vue/custom-event-name-casing
  emit("search-site-name", value);
}, 500);
</script>

<template>
  <div class="bg-[#F5FBFF] flex h-screen items-center justify-center w-screen">
    <div class="flex flex-col gap-8 items-center max-h-screen">
      <img
        :src="RocketLogo"
        alt="Rocket Alumni Solutions"
        class="h-auto w-24"
      />
      <div
        class="border border-[#DFEAF2] flex flex-col h-[500px] p-6 rounded-xl shadow-sm w-[460px]"
      >
        <Text
          variant="title2"
          class="font-semibold mb-4 text-[#002B4D] text-center"
          >Welcome Back</Text
        >

        <Text v-if="!isInternal" variant="body" class="opacity-75 text-center">
          Select your site below to get started
        </Text>

        <Text v-else variant="body" class="opacity-75 text-center">
          Search for a site below to get started...
        </Text>

        <Input
          v-if="isInternal"
          v-model="searchQuery"
          class="bg-slate-50/25 border border-slate-200 duration-300 mt-8 p-6 text-lg"
          placeholder="Search for a site"
          autofocus
          data-testid="site-switcher-fullpage-search-input"
          @input="debouncedSearch(searchQuery)"
        />

        <div
          class="flex flex-col gap-4 mt-8 overflow-hidden overflow-y-scroll relative"
          data-testid="site-switcher-fullpage-search-results"
        >
          <LoadingSpinner
            v-if="isInternalSiteSearchLoading"
            size="lg"
            class="m-auto"
          />

          <button
            v-for="(site, s) in sites"
            v-else
            :key="site._id"
            class="border cursor-pointer duration-300 flex flex-shrink-0 gap-3 h-16 hover:bg-slate-50 hover:border-slate-200 items-center justify-start px-2 rounded-lg text-ellipsis transition-all"
            @click="emit('go-to-site', site)"
            @mouseenter="hoverKey = s"
            @focus="hoverKey = s"
            @mouseleave="hoverKey = -1"
            @blur="hoverKey = -1"
          >
            <div
              class="border border-slate-200 h-8 overflow-hidden rounded-full w-8"
            >
              <img
                v-if="site.primaryLogo?.[0]?.uuid"
                :src="`https://media.rocketalumnisolutions.com/${site.primaryLogo?.[0]?.uuid}/-/resize/80x/`"
                :alt="site.title"
                class="h-full object-cover w-full"
              />
            </div>

            <Text variant="body">{{ site.styleName }}</Text>

            <ChevronRight
              class="duration-200 h-4 ml-auto opacity-0 transition-all translate-x-[-10px] w-4"
              :class="{
                'translate-x-[0px] opacity-50': hoverKey === s,
              }"
            />
          </button>
        </div>
      </div>

      <Text
        v-if="!isInternal"
        variant="body"
        class="opacity-75 text-center text-gray-700"
      >
        Need help? Contact our support team
        <a
          href="https://help.rocketalumnisolutions.com/"
          class="text-blue-600"
          target="_blank"
          >here</a
        >.
      </Text>

      <Text v-else variant="body" class="opacity-75 text-center text-gray-700">
        You are signed in as an internal user. Proceed with caution!
      </Text>
    </div>
  </div>
</template>
